import React, { useState, useRef, useEffect } from "react";

interface MultiSelectOption {
  title: string;
  key: string;
}
interface MultiSelectProps {
  options: MultiSelectOption[];
  selectedValues: string[];
  onChange: (selected: string[]) => void;
  title?: string;
  required?: boolean;
  errorMsg?: string;
  isPaginated?: boolean;
  onPageChange?: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
}

const MultiSelect = ({ options, selectedValues, onChange, title, required = false, errorMsg, isPaginated, onPageChange }: MultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => setIsOpen(!isOpen);

  const handleOptionClick = (key: string) => {
    const updated = selectedValues.includes(key) ? selectedValues.filter(item => item !== key) : [...selectedValues, key];
    onChange(updated);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      {title && (
        <label className="text-sm font-medium block text-docsigna-blue-dark mb-1">
          {title}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div
        className={`w-full border rounded-md px-3 py-2 cursor-pointer font-medium border-docsigna-blue-dark
           `}
        onClick={toggleDropdown}
      >
        {selectedValues.length > 0
          ? options
              .filter(option => selectedValues.includes(option.key))
              .map(option => option.title)
              .join(", ")
          : "Select options"}
      </div>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-md max-h-60 overflow-auto">
          {options.map((option, index) => (
            <label key={index} className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm">
              <input type="checkbox" checked={selectedValues.includes(option.key)} onChange={() => handleOptionClick(option.key)} className="mr-2" />
              {option.title}
            </label>
          ))}
          {isPaginated && (
            <div className="flex justify-end px-3 mb-1 text-blue-900 cursor-pointer" onClick={e => onPageChange?.(null, 1)}>
              More
            </div>
          )}
        </div>
      )}
      {errorMsg && <p className="text-red-500 text-sm mt-1">{errorMsg}</p>}
    </div>
  );
};

export default MultiSelect;
